import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Shield, Save, Eye, PlusCircle, Edit3, Trash2, Globe, Users, User as UserIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MODULE_LABELS_RU = {
  dashboard: 'Главная',
  clients: 'Клиенты',
  calendar: 'Календарь',
  applications: 'Заявки',
  history: 'История',
  integration: 'Интеграция',
  staff: 'Сотрудники',
  access: 'Доступ',
  trading_management: 'Торговая платформа',
  trading_accounts: 'Торговые аккаунты',
  voice_agent: 'AI Голосовой агент',
  stats: 'Статистика по отделам',
};

const ACTION_ICONS = {
  view: Eye,
  create: PlusCircle,
  edit: Edit3,
  delete: Trash2,
};

const VISIBILITY_OPTIONS = [
  { value: 'own', labelKey: 'Только свои клиенты', icon: UserIcon },
  { value: 'team', labelKey: 'Клиенты своего отдела', icon: Users },
  { value: 'all', labelKey: 'Все клиенты', icon: Globe },
];

const PermissionsModal = ({ user, onClose, onSaved }) => {
  const { tt } = useLanguage();
  const [permissions, setPermissions] = useState(null);
  const [modules, setModules] = useState([]);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/users/${user.id}/permissions`);
        setPermissions(res.data.permissions);
        setModules(res.data.available_modules || []);
        setActions(res.data.available_actions || []);
      } catch (e) {
        setError(e.response?.data?.detail || tt('Ошибка загрузки прав доступа'));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user.id]);

  const toggleAction = (module, action) => {
    setPermissions(prev => ({
      ...prev,
      modules: {
        ...prev.modules,
        [module]: {
          ...prev.modules[module],
          [action]: !prev.modules[module]?.[action],
        },
      },
    }));
  };

  const setVisibility = (value) => {
    setPermissions(prev => ({ ...prev, client_visibility: value }));
  };

  const applyPreset = (preset) => {
    const fullModules = {};
    modules.forEach(m => {
      if (preset === 'full') {
        fullModules[m] = { view: true, create: true, edit: true, delete: true };
      } else if (preset === 'view_only') {
        fullModules[m] = { view: true, create: false, edit: false, delete: false };
      } else if (preset === 'none') {
        fullModules[m] = { view: false, create: false, edit: false, delete: false };
      }
    });
    setPermissions(prev => ({ ...prev, modules: fullModules }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await axios.put(`${API}/users/${user.id}/permissions`, { permissions });
      if (onSaved) onSaved();
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || tt('Ошибка сохранения прав'));
    } finally {
      setSaving(false);
    }
  };

  const handleResetToRoleDefaults = async () => {
    if (!window.confirm(tt('Сбросить права доступа к настройкам по умолчанию для этой роли?'))) return;
    setSaving(true);
    setError('');
    try {
      const res = await axios.post(`${API}/users/${user.id}/permissions/reset`);
      setPermissions(res.data.permissions);
    } catch (e) {
      setError(e.response?.data?.detail || tt('Ошибка сброса'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="permissions-modal">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-100 dark:bg-indigo-900 rounded-lg flex items-center justify-center">
              <Shield className="w-5 h-5 text-indigo-600 dark:text-indigo-300" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {tt('Права доступа сотрудника')}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {user.name} • <span className="font-mono">{user.role}</span>
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200" data-testid="close-permissions">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {loading ? (
            <div className="text-center py-10 text-gray-500">{tt('Загрузка...')}</div>
          ) : !permissions ? (
            <div className="text-center py-10 text-red-600">{error || tt('Нет данных')}</div>
          ) : (
            <>
              {/* Quick presets */}
              <div>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                  {tt('Быстрые шаблоны')}
                </p>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => applyPreset('full')}
                    className="px-3 py-1.5 text-xs rounded-md border border-green-300 bg-green-50 text-green-700 hover:bg-green-100 dark:bg-green-900/30 dark:text-green-300 dark:border-green-700"
                    data-testid="preset-full"
                  >
                    {tt('Полный доступ')}
                  </button>
                  <button
                    onClick={() => applyPreset('view_only')}
                    className="px-3 py-1.5 text-xs rounded-md border border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700"
                    data-testid="preset-view-only"
                  >
                    {tt('Только просмотр')}
                  </button>
                  <button
                    onClick={() => applyPreset('none')}
                    className="px-3 py-1.5 text-xs rounded-md border border-red-300 bg-red-50 text-red-700 hover:bg-red-100 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700"
                    data-testid="preset-none"
                  >
                    {tt('Без доступа')}
                  </button>
                </div>
              </div>

              {/* Client visibility */}
              <div>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                  {tt('Видимость клиентов')}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {VISIBILITY_OPTIONS.map(opt => {
                    const Icon = opt.icon;
                    const active = permissions.client_visibility === opt.value;
                    return (
                      <button
                        key={opt.value}
                        onClick={() => setVisibility(opt.value)}
                        className={`flex items-center gap-2 px-3 py-2.5 rounded-md border text-sm transition ${
                          active
                            ? 'border-indigo-500 bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300'
                            : 'border-gray-200 hover:border-gray-300 text-gray-700 dark:border-gray-600 dark:text-gray-200'
                        }`}
                        data-testid={`visibility-${opt.value}`}
                      >
                        <Icon className="w-4 h-4" />
                        <span>{tt(opt.labelKey)}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Modules matrix */}
              <div>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                  {tt('Модули системы')}
                </p>
                <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-900">
                      <tr>
                        <th className="text-left p-3 font-medium text-gray-700 dark:text-gray-200">
                          {tt('Модуль')}
                        </th>
                        {actions.map(action => {
                          const Icon = ACTION_ICONS[action];
                          return (
                            <th key={action} className="p-3 text-center font-medium text-gray-700 dark:text-gray-200">
                              <div className="flex flex-col items-center gap-1">
                                {Icon && <Icon className="w-4 h-4" />}
                                <span className="capitalize text-xs">{tt(action === 'view' ? 'Просмотр' : action === 'create' ? 'Создание' : action === 'edit' ? 'Редактирование' : 'Удаление')}</span>
                              </div>
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {modules.map(module => (
                        <tr key={module} className="border-t border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-900">
                          <td className="p-3 font-medium text-gray-900 dark:text-gray-100">
                            {tt(MODULE_LABELS_RU[module] || module)}
                          </td>
                          {actions.map(action => (
                            <td key={action} className="p-3 text-center">
                              <input
                                type="checkbox"
                                className="w-4 h-4 accent-indigo-600 cursor-pointer"
                                checked={!!permissions.modules?.[module]?.[action]}
                                onChange={() => toggleAction(module, action)}
                                data-testid={`perm-${module}-${action}`}
                              />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {error && (
            <div className="p-3 rounded-md bg-red-50 text-red-700 text-sm dark:bg-red-900/30 dark:text-red-300">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-wrap items-center justify-between gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={handleResetToRoleDefaults}
            disabled={saving || loading}
            className="text-sm text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-200 disabled:opacity-50"
            data-testid="reset-to-role-defaults"
          >
            {tt('Вернуть права по умолчанию для роли')}
          </button>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="btn btn-secondary"
              data-testid="cancel-permissions"
            >
              {tt('Отмена')}
            </button>
            <button
              onClick={handleSave}
              disabled={saving || loading || !permissions}
              className="btn btn-primary flex items-center gap-2 disabled:opacity-50"
              data-testid="save-permissions"
            >
              <Save className="w-4 h-4" />
              {saving ? tt('Сохранение...') : tt('Сохранить')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PermissionsModal;
